import React from 'react';
import { observer } from 'mobx-react-lite';
import FilteredTaskList from '../filteredTaskList';
import OtherTaskList from '../otherTaskList';
import { SHOW_TASK_GROUPING_TYPE } from '../../../../../global/constants';
import { OptionDiv, Show, Filter, TaskNumber, Text2 } from '../../styles';
import type {
  MobxFilteredTasksListInterface,
  MobxFilteredCompletedTasksListInterface,
  NewTaskFilterInterface,
  SubtaskDataResult
} from '../../../../../interfaces/TaskInterface';
import type { TaskModel } from '../../../../../mobx/models/task';
import type { WorkspaceModel } from '../../../../../mobx/models/workspace';
import type { ProjectModel } from '../../../../../mobx/models/project';
import type { ProjectTemplateModel } from '../../../../../mobx/models/projectTemplate';
import type { StatusListInterface } from '../../../../../interfaces/ProjectInterface';
import type { TaskGroupModalActions } from '../taskGroup';

const EMPTY_SUBTASK: { id: string; showCompleted: boolean } = { id: '', showCompleted: false };

interface Props {
  filteredTasksList?: MobxFilteredTasksListInterface[];
  otherTask?: MobxFilteredCompletedTasksListInterface;
  taskFilter?: NewTaskFilterInterface;
  hiddenTask?: number;
  handleShowOptions: () => void;
  // pass-through props for inner lists
  collapseIds: string[];
  handleCollapseClick: (id: string) => void;
  loadData: () => void;
  currentWorkspace: WorkspaceModel | null;
  isTemplateProject?: boolean;
  currentProject: ProjectModel | ProjectTemplateModel | null;
  isView?: boolean;
  showProjectName?: boolean;
  selectedId?: string;
  showCompletedTask?: boolean;
  openSubTask?: { id: string; showCompleted: boolean };
  isSelected: (groupId: string, taskId: string) => boolean;
  handleHoverTask: (groupId: string, taskId: string, isCompleted?: boolean) => void;
  onClickTaskCheckBox: (task: TaskModel) => void;
  onChangeStatus: (task: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => void;
  handleSubtaskOpen: (id: string) => void;
  handleShowCompletedSubTask: (id: string, isShow: boolean) => void;
  toggleCompletedTask: (show: boolean, comparedValue: string) => void;
  getSubtaskData: (task: TaskModel) => SubtaskDataResult;
  modalActions: TaskGroupModalActions;
  maxVisibleLabels: number;
  setOpen: (value: boolean) => void;
  setDeleteModalOpen: (value: boolean) => void;
  setSelectedItem: (value: TaskModel) => void;
}

const TaskList = observer(function TaskList(props: Props) {
  const {
    filteredTasksList,
    otherTask,
    taskFilter,
    hiddenTask = 0,
    handleShowOptions,
    collapseIds,
    handleCollapseClick,
    loadData,
    currentWorkspace,
    isTemplateProject,
    currentProject,
    isView,
    showProjectName,
    selectedId,
    showCompletedTask,
    openSubTask,
    isSelected,
    handleHoverTask,
    onClickTaskCheckBox,
    onChangeStatus,
    handleSubtaskOpen,
    handleShowCompletedSubTask,
    toggleCompletedTask,
    getSubtaskData,
    modalActions,
    maxVisibleLabels,
    setOpen,
    setDeleteModalOpen,
    setSelectedItem
  } = props;

  const hasItems =
    (filteredTasksList && filteredTasksList.length > 0) ||
    (otherTask?.tasks && otherTask?.tasks.length > 0) ||
    (otherTask?.completedTasks && otherTask?.completedTasks.length > 0) ||
    taskFilter?.showEmptyGroup;

  if (!hasItems) return null;

  return (
    <>
      <FilteredTaskList
        filteredTasksList={filteredTasksList}
        collapseIds={collapseIds}
        handleCollapseClick={handleCollapseClick}
        loadData={loadData}
        currentWorkspace={currentWorkspace}
        taskFilter={taskFilter}
        isTemplateProject={isTemplateProject}
        currentProject={currentProject}
        isView={isView}
        showProjectName={showProjectName}
        selectedId={selectedId || ''}
        showCompletedTask={!!showCompletedTask}
        openSubTask={openSubTask || EMPTY_SUBTASK}
        isSelected={isSelected}
        handleHoverTask={handleHoverTask}
        onClickTaskCheckBox={onClickTaskCheckBox}
        onChangeStatus={onChangeStatus}
        handleSubtaskOpen={handleSubtaskOpen}
        handleShowCompletedSubTask={handleShowCompletedSubTask}
        toggleCompletedTask={toggleCompletedTask}
        getSubtaskData={getSubtaskData}
        modalActions={modalActions}
        maxVisibleLabels={maxVisibleLabels}
      />

      {otherTask && (
        <OtherTaskList
          otherTask={otherTask}
          groupingStatus={taskFilter?.groupingStatus}
          loadData={loadData}
          isTemplateProject={isTemplateProject}
          currentProject={currentProject}
          setOpen={setOpen}
          setDeleteModalOpen={setDeleteModalOpen}
          setSelectedItem={setSelectedItem}
          handleSubtaskOpen={handleSubtaskOpen}
          openSubTask={openSubTask}
          taskFilter={taskFilter}
          onClickTaskCheckBox={onClickTaskCheckBox}
          onChangeStatus={onChangeStatus}
          isView={isView}
          showProjectName={showProjectName}
          getSubtaskData={getSubtaskData}
          selectedId={selectedId}
          showCompletedTask={showCompletedTask}
          handleShowCompletedSubTask={handleShowCompletedSubTask}
          toggleCompletedTask={toggleCompletedTask}
          maxVisibleLabels={maxVisibleLabels}
        />
      )}

      {((filteredTasksList && filteredTasksList.length > 0) || (otherTask?.tasks && otherTask?.tasks.length > 0)) &&
        taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS &&
        hiddenTask > 0 && (
          <OptionDiv>
            <Filter>
              <TaskNumber>{hiddenTask} Tasks</TaskNumber>
              <Text2>hidden by filter options</Text2>
            </Filter>
            <Show role='button' onClick={handleShowOptions}>
              <p>Show options</p>
            </Show>
          </OptionDiv>
        )}
    </>
  );
});

export default TaskList;
